import { PrimaryButton } from "@certn/cascadia-react";
import { components } from "@certn/legacy-api/generated-schemas/people";
import { slugify } from "@certn/utils";
import { t } from "i18next";
import { object, string } from "yup";

import { EmployerReferenceInputField } from "./EmployerReferenceInputField";
import { EmployerReferencePreSaveModal } from "./EmployerReferencePreSaveModal";
import { EmployerReferenceSummaryField } from "./EmployerReferenceSummaryField";
import { getReferencesRuleResults } from "./utils";
import { FieldType } from "../types";

export function EmploymentReferenceField(
  field: components["schemas"]["EmploymentReferencesCheckField"]
): FieldType<components["schemas"]["EmploymentReferencesCheckField"]> {
  return {
    field,
    inputElement: (
      <EmployerReferenceInputField key={field.name} field={field} />
    ),
    summaryElement: () => (
      <EmployerReferenceSummaryField
        key={field.name + "_summary_field"}
        field={field}
      />
    ),
    schema: employerReferenceFieldSchema(field),
    // override for ActionBar Next to allow additional Modal + isolated validation
    // values is required here so when a reference is deleted this modal updates
    saveButtonOverride: ({ values, submitForm }) => {
      const references = values[field.name]?.references;
      const { isValid } = getReferencesRuleResults({
        field,
        values: references,
      });

      if (!isValid)
        return (
          <EmployerReferencePreSaveModal
            key={field.name + "_presave_modal"}
            field={field}
            handleNext={submitForm}
            trigger={
              <PrimaryButton
                size="medium"
                testID={`${slugify(field.name)}-section-next-step-button`}
              >
                {t("Next step")}
              </PrimaryButton>
            }
          />
        );
      return undefined;
    },
    initialValue: field.value,
  };
}

export function employerReferenceFieldSchema(
  field: components["schemas"]["EmploymentReferencesCheckField"]
) {
  return object({
    employer_reference_skip_reason: string().when(
      ["references"],
      ([values]) => {
        const { isValid } = getReferencesRuleResults({ field, values });

        if (!isValid) {
          return string().required(t("Please select a reason"));
        }
        return string().notRequired();
      }
    ),
  });
}
